import { cn } from "@skypointcloud/skypoint-ui";
import { useTheme, type ThemePreference } from "@/contexts/ThemeContext";
import { DarkModeIcon, LightModeIcon, SystemModeIcon, type IconComponent } from "@/lib/icons";

const OPTIONS: ReadonlyArray<{ value: ThemePreference; label: string; icon: IconComponent }> = [
  { value: "light", label: "Light", icon: LightModeIcon },
  { value: "dark", label: "Dark", icon: DarkModeIcon },
  { value: "system", label: "System", icon: SystemModeIcon },
];

/**
 * Light / Dark / System segmented control, shown under "Appearance" in the
 * top-bar user menu (UX-2). Reads and writes the app-wide theme through
 * {@link useTheme}; every class here is a semantic token, so the control themes
 * itself along with everything else.
 *
 * A `<fieldset>` (role group) with `aria-pressed` rather than a radiogroup: each option stays
 * an ordinary tab stop, screen readers announce its pressed state, and there is
 * no roving-tabindex or arrow-key handling to get wrong.
 */
export function ThemeToggle() {
  const { preference, setPreference } = useTheme();

  return (
    <fieldset
      aria-label="Appearance"
      className="min-w-0 mx-4 my-1 flex items-center gap-1 rounded-md border border-border bg-muted p-1"
    >
      {OPTIONS.map(({ value, label, icon: Icon }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            aria-label={`${label} theme`}
            onClick={() => setPreference(value)}
            data-testid={`theme-option-${value}`}
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 rounded-sm px-2 py-1.5 text-xs font-medium transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              active
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            <span>{label}</span>
          </button>
        );
      })}
    </fieldset>
  );
}
